import { Box, Group, Text, Badge, Stack, CopyButton, ActionIcon, Tooltip, Skeleton, Button } from '@mantine/core'
import { ResizableDrawer } from '@/components/ui'
import { IconCopy, IconCheck, IconExternalLink } from '@tabler/icons-react'
import { LineChart } from '@mantine/charts'
import { useQuery } from '@tanstack/react-query'
import { useState, useEffect } from 'react'
import { api } from '@/api/client'
import { useCurrentTenant } from '@/store/auth'
import { DashboardCard } from '@/components/ui'
import { targetGroupDiagnosticsApi } from '@/api/targetGroupDiagnostics'
import { TgDiagnosticsPanel } from './TgDiagnosticsPanel'

interface NodeDrawerProps {
  opened: boolean
  onClose: () => void
  nodeId: string | null
  nodeData: {
    label: string
    type: string
    status: string
    arn?: string
    instance_id?: string
    instance_type?: string
    private_ip?: string
    state?: string
    dns_name?: string
    scheme?: string
    protocol?: string
    port?: number
    healthy?: number
    total?: number
    engine?: string
    db_identifier?: string
    zone_id?: string
    private_zone?: boolean
    [key: string]: unknown
  } | null
}

const statusColor: Record<string, string> = {
  healthy: 'teal',
  warning: 'yellow',
  critical: 'red',
}

export function NodeDrawer({ opened, onClose, nodeId, nodeData }: NodeDrawerProps) {
  const tenant = useCurrentTenant()

  // Fetch node-specific metrics when drawer opens
  const cwMetrics = useQuery({
    queryKey: ['infra-metrics', tenant?.id],
    queryFn: async () => {
      const res = await api.get(`/v1/tenants/${tenant!.id}/infrastructure/metrics`)
      return res.data
    },
    enabled: !!tenant?.id && opened,
    staleTime: 300_000,
  })

  if (!nodeData) return null

  const cw = cwMetrics.data
  const type = nodeData.type

  return (
    <ResizableDrawer
      storageKey="node-drawer-width"
      defaultWidth={400}
      minWidth={350}
      opened={opened}
      onClose={onClose}
      position="right"
      title={
        <Group gap="xs">
          <Text fw={700}>{nodeData.label}</Text>
          <Badge color={statusColor[nodeData.status] || 'gray'} size="sm">
            {nodeData.type.toUpperCase()}
          </Badge>
        </Group>
      }
      overlayProps={{ backgroundOpacity: 0.1 }}
    >
      <Stack gap="md">
        {/* EC2 Details */}
        {type === 'ec2' && (
          <>
            <Group gap="xs">
              <Badge variant="light" size="sm">{nodeData.instance_type}</Badge>
              <Badge variant="light" color={nodeData.state === 'running' ? 'green' : 'red'} size="sm">
                {nodeData.state}
              </Badge>
            </Group>
            {nodeData.private_ip && (
              <Text size="sm" c="dimmed">IP: {nodeData.private_ip}</Text>
            )}
            {cw?.ec2 && (
              <>
                <DashboardCard title="CPU Médio" value={cw.ec2.cpu_avg != null ? `${cw.ec2.cpu_avg}%` : '—'} status={cw.ec2.cpu_avg >= 80 ? 'error' : cw.ec2.cpu_avg >= 60 ? 'warning' : 'success'} />
                <DashboardCard title="Status Check" value={cw.ec2.status_check_failed ?? 0} status={cw.ec2.status_check_failed > 0 ? 'error' : 'success'} />
                {cw.ec2.datapoints?.length > 0 && (
                  <Box>
                    <Text size="xs" c="dimmed" mb={4}>CPU 24h</Text>
                    <LineChart
                      h={80}
                      data={cw.ec2.datapoints.map((v: number, i: number) => ({ h: `${i}h`, cpu: v }))}
                      dataKey="h"
                      series={[{ name: 'cpu', color: 'teal.6' }]}
                      curveType="monotone"
                      withXAxis={false}
                      withYAxis={false}
                      withDots={false}
                      withTooltip
                    />
                  </Box>
                )}
              </>
            )}
          </>
        )}

        {/* ALB Details */}
        {type === 'alb' && (
          <>
            {nodeData.dns_name && <Text size="sm" c="dimmed">DNS: {nodeData.dns_name}</Text>}
            <Badge variant="light" size="sm">{nodeData.scheme}</Badge>
            {cw?.alb && (
              <>
                <DashboardCard title="Response Time P95" value={cw.alb.response_time_p95_ms != null ? `${cw.alb.response_time_p95_ms} ms` : '—'} status={cw.alb.response_time_p95_ms >= 500 ? 'error' : 'success'} />
                <DashboardCard title="Requests/min" value={cw.alb.requests_per_min ?? '—'} status="success" />
                <DashboardCard title="5XX Errors" value={cw.alb.http_5xx ?? 0} status={cw.alb.http_5xx > 0 ? 'error' : 'success'} />
                <DashboardCard title="Targets" value={`${cw.alb.healthy_targets}/${cw.alb.total_targets}`} status={cw.alb.unhealthy_targets > 0 ? 'error' : 'success'} />
              </>
            )}
          </>
        )}

        {/* RDS Details */}
        {type === 'rds' && (
          <>
            <Badge variant="light" size="sm">{nodeData.engine}</Badge>
            {cw?.rds && (
              <>
                <DashboardCard title="CPU" value={cw.rds.cpu != null ? `${cw.rds.cpu}%` : '—'} status={cw.rds.cpu >= 80 ? 'error' : 'success'} />
                <DashboardCard title="Conexões" value={cw.rds.connections ?? '—'} status="success" />
                <DashboardCard title="Memória Livre" value={cw.rds.freeable_memory_gb != null ? `${cw.rds.freeable_memory_gb} GB` : '—'} status={cw.rds.freeable_memory_gb != null && cw.rds.freeable_memory_gb < 1 ? 'warning' : 'success'} />
              </>
            )}
          </>
        )}

        {/* Target Group Details */}
        {type === 'target_group' && (
          <TargetGroupSection tenantId={tenant?.id} nodeData={nodeData} />
        )}

        {/* Domain Details */}
        {type === 'domain' && (
          <>
            <Badge variant="light" size="sm">{nodeData.private_zone ? 'Private' : 'Public'}</Badge>
          </>
        )}

        {/* S3 Details (TASK-2604) */}
        {type === 's3' && (
          <>
            {nodeData.region != null && (
              <Text size="sm" c="dimmed">Região: {nodeData.region as string}</Text>
            )}
            <Badge
              variant="light"
              size="sm"
              color={(nodeData.versioning as string) === 'Enabled' ? 'green' : 'gray'}
            >
              Versionamento: {(nodeData.versioning as string) ?? '—'}
            </Badge>
          </>
        )}

        {/* ARN (all types) */}
        {nodeData.arn && (
          <Box mt="md" p="xs" style={{ backgroundColor: 'var(--mantine-color-dark-7)', borderRadius: 4 }}>
            <Group justify="space-between" gap="xs">
              <Text size="xs" c="dimmed" style={{ wordBreak: 'break-all', flex: 1 }}>
                {nodeData.arn}
              </Text>
              <CopyButton value={nodeData.arn as string}>
                {({ copied, copy }) => (
                  <Tooltip label={copied ? 'Copiado!' : 'Copiar ARN'}>
                    <ActionIcon size="sm" variant="subtle" onClick={copy}>
                      {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                    </ActionIcon>
                  </Tooltip>
                )}
              </CopyButton>
            </Group>
          </Box>
        )}
      </Stack>
    </ResizableDrawer>
  )
}

function TargetGroupSection({ tenantId, nodeData }: { tenantId?: number; nodeData: NodeDrawerProps['nodeData'] }) {
  const [run, setRun] = useState(false)
  const diag = useQuery({
    queryKey: ['tg-diagnose', tenantId, nodeData?.arn],
    queryFn: () => targetGroupDiagnosticsApi.diagnose(tenantId!, nodeData!.arn as string),
    enabled: run && !!tenantId && !!nodeData?.arn,
    staleTime: 60_000,
  })
  // Reset run flag when target group changes so diagnosis only fires on explicit click
  useEffect(() => { setRun(false) }, [nodeData?.arn])
  if (!nodeData) return null
  return (
    <>
      <Text size="sm">Protocolo: {nodeData.protocol} / Porta: {nodeData.port}</Text>
      <DashboardCard
        title="Targets Saudáveis"
        value={`${nodeData.healthy ?? 0}/${nodeData.total ?? 0}`}
        status={(nodeData.healthy ?? 0) < (nodeData.total ?? 0) ? 'error' : 'success'}
      />
      {(nodeData.hc_port || nodeData.hc_path) && (
        <Text size="xs" c="dimmed">
          Health check: {String(nodeData.hc_protocol || '')} {String(nodeData.hc_port || '')}
          {nodeData.hc_path ? ` ${nodeData.hc_path}` : ''}
          {nodeData.hc_matcher ? ` (matcher ${nodeData.hc_matcher})` : ''}
        </Text>
      )}
      <Button size="xs" variant="light" onClick={() => setRun(true)} loading={diag.isFetching}>
        Diagnosticar
      </Button>
      {run && (
        <TgDiagnosticsPanel
          loading={diag.isLoading}
          error={diag.error as Error | null}
          data={diag.data}
        />
      )}
    </>
  )
}
